import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SlideFade,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ChevronRightIcon, CopyIcon } from '@chakra-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { ActionIconButton } from './Styles';
import { MoreOutlined } from '@ant-design/icons';
import { FiMoreVertical } from 'react-icons/fi';
import API from '../../../context/API';
import EditOptions from './EditOptions/EditOptions';
import AddToItems from './AddToItems';
import CustomizationRules from './CustomizationRules';
import { CustomizationData, RelatedItem } from '../../../redux/Customizations';

interface CustomizationSideBoxProps {
  customizationData: CustomizationData;
  handleInputChange: (field: string, value: any) => void;
  handleDelete: () => void;
  handleDuplicate: () => void | Promise<void>;
  handleSeeChanges: () => void;
  onClose: () => void;
  isOpen: boolean;
  handleSubmit: (submissionType: string, data: any) => void | Promise<void>;
  setCustomizationData: (data: CustomizationData | null | ((prev: CustomizationData | null) => CustomizationData | null)) => void;
  handleEdit: (name: string) => void;
  isNewCustomization: boolean;
  selectedItem: number | null;
  handleRefresh: () => void | Promise<void>;
  width?: string;
  isEditOption: boolean;
  autoEditOptions?: boolean;
}

const CustomizationSideBox: React.FC<CustomizationSideBoxProps> = ({
  customizationData,
  handleInputChange,
  handleDelete,
  handleDuplicate,
  handleSeeChanges,
  onClose,
  isOpen,
  handleSubmit,
  setCustomizationData,
  handleEdit,
  isNewCustomization,
  selectedItem,
  handleRefresh,
  width,
  isEditOption,
  autoEditOptions = false,
}) => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [options, setOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [editOptionsMode, setEditOptionsMode] = useState(isEditOption || false);
  const [isOption, setIsOption] = useState(true);
  const [addToItemsMode, setAddToItemsMode] = useState(false);
  const [customizationRulesMode, setCustomizationRulesMode] = useState(false);
  const [sold_out, setSoldOut] = useState(customizationData?.sold_out || false);

  const borderColor = useColorModeValue('gray.300', 'gray.700');

  useEffect(() => {
    if (isNewCustomization) {
      setSelectedOptions([]);
    }
  }, [isNewCustomization]);

  // Auto-navigate to edit options when edit button is clicked
  useEffect(() => {
    if (autoEditOptions && customizationData) {
      setEditOptionsMode(true);
    }
  }, [autoEditOptions, customizationData]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await API.get('/options');
        if (response.data.success) {
          setOptions(response.data.data);
          setFilteredOptions(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };

    fetchOptions();
  }, []);

  const handleEditNameSave = async () => {
    if (!customizationData) {
      console.error('❌ No customization data available for name update');
      return;
    }

    setIsSavingName(true);
    try {
      await handleSubmit('name', customizationData);
      setIsEditModalOpen(false);
    } finally {
      setIsSavingName(false);
    }
  };

  useEffect(() => {
    if (
      customizationData &&
      (customizationData.options || customizationData.option) &&
      options.length > 0
    ) {
      const optionsData = customizationData.options || customizationData.option || [];
      const selectedOptionItems = options.filter((option) =>
        optionsData.includes(option.id.toString())
      );
      setSelectedOptions(selectedOptionItems);
    }
  }, [customizationData.options, customizationData.option, options]);

  const openEditModal = () => {
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditOptionsClick = () => {
    setEditOptionsMode(true);
  };

  const handleCustomizationRulesClick = () => {
    setCustomizationRulesMode(true);
  };

  const handleAddToItemsClick = () => {
    setAddToItemsMode(true);
  };

  const handleBackClick = () => {
    setEditOptionsMode(false);
    setAddToItemsMode(false);
    setCustomizationRulesMode(false);
    setIsOption(false);
  };

  const handleStockToggle = async (customizationId: number, soldOutValue: boolean) => {
    try {
      console.log(`🔄 Toggling stock for customization ${customizationId}:`, soldOutValue);

      const response = await API.get(`/customizations/stock-toggle?customization=${customizationId}`);

      console.log('🔍 Stock toggle response:', response);

      if (response.data.success) {
        const updatedCustomization = response.data.data;
        console.log('✅ Stock toggled successfully:', updatedCustomization);

        setSoldOut(updatedCustomization.sold_out);
        handleInputChange('sold_out', updatedCustomization.sold_out);

        setCustomizationData((prev) => ({
          ...prev!,
          sold_out: updatedCustomization.sold_out,
          updatedAt: updatedCustomization.updatedAt,
        }));

        toast({
          title: 'Stock status updated',
          description: `Customization is now ${updatedCustomization.sold_out ? 'sold out' : 'available'}.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        window.dispatchEvent(
          new CustomEvent('customizationUpdated', {
            detail: { customization: updatedCustomization },
          })
        );
      } else {
        throw new Error('API response indicates failure');
      }
    } catch (error: any) {
      console.error('❌ Error toggling stock:', error);
      console.error('❌ Error response:', (error as any).response);

      setSoldOut(!soldOutValue);
      handleInputChange('sold_out', !soldOutValue);

      toast({
        title: 'Error updating stock status',
        description: (error as any).response?.data?.message || 'Failed to update stock status. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRelatedItemsChange = (updatedItems: RelatedItem[]) => {
    setCustomizationData((prevData) => ({
      ...prevData!,
      related_items: updatedItems,
    }));
  };

  // Safety check - don't render if customizationData is not available
  if (!customizationData) {
    return null;
  }

  // Conditional rendering instead of early returns
  if (editOptionsMode || (isEditOption && isOption)) {
    return (
      <EditOptions
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        selectedItem={selectedItem}
        handleRefresh={handleRefresh}
        width={width}
      />
    );
  }

  if (addToItemsMode) {
    return (
      <AddToItems
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        handleSubmit={handleSubmit}
        handleRelatedItemsChange={handleRelatedItemsChange}
        width={width}
      />
    );
  }

  if (customizationRulesMode) {
    return (
      <CustomizationRules
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        width={width}
      />
    );
  }

  return (
    <Box
      width={width || '25%'}
      position={width ? undefined : 'fixed'}
      right={width ? 1 : 4}
      h={`calc(100vh - 120px)`}
      maxWidth={'inherit'}
      borderLeft={width ? '' : '1px solid'}
      borderColor={borderColor}
      overflowY={'auto'}
    >
      <SlideFade in={true} offsetY="20px">
        <Box p={{ base: 3, sm: 4 }}>
          <Flex
            justify="space-between"
            align="center"
            mb={4}
            direction={{ base: 'column', sm: 'row' }}
            gap={2}
          >
            <HStack>
              <Heading size={{ base: 'sm', sm: 'md' }}>Customizations</Heading>
            </HStack>
            <HStack spacing={2}>
              <Button
                leftIcon={<CopyIcon />}
                size={{ base: 'xs', sm: 'sm' }}
                onClick={handleDuplicate}
                colorScheme="green"
                variant="outline"
                _hover={{ bg: 'green.50', borderColor: 'green.300' }}
              >
                Duplicate
              </Button>
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label="More options"
                  variant="ghost"
                  icon={<MoreOutlined />}
                />
                <MenuList>
                  <MenuItem onClick={handleDelete}>Delete</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
          <VStack align="start" spacing={4}>
            <Box w="full">
              <HStack justify="space-between" mb={1}>
                <Text fontSize="md" fontWeight="bold">
                  Customization Name
                </Text>
                <ActionIconButton
                  iconColor="#2D6CCA"
                  onClick={openEditModal}
                >
                  <FontAwesomeIcon icon={faPen} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
                </ActionIconButton>
              </HStack>
              <Text
                fontSize="sm"
                bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}
                p={2}
                borderRadius="md"
              >
                {customizationData.customization}
              </Text>
            </Box>

            <Button
              width="full"
              variant="outline"
              justifyContent="space-between"
              rightIcon={<ChevronRightIcon />}
              onClick={handleEditOptionsClick}
              size={{ base: 'sm', sm: 'md' }}
            >
              Edit options
            </Button>
            <Text px={{ base: 2, sm: 4 }} fontSize={{ base: 'xs', sm: 'sm' }}>
              Manage which options are available to customers in this set.
            </Text>

            <Button
              width="full"
              variant="outline"
              justifyContent="space-between"
              rightIcon={<ChevronRightIcon />}
              onClick={handleAddToItemsClick}
              size={{ base: 'sm', sm: 'md' }}
            >
              Add to items
            </Button>

            <Button
              width="full"
              variant="outline"
              justifyContent="space-between"
              rightIcon={<ChevronRightIcon />}
              onClick={handleCustomizationRulesClick}
              size={{ base: 'sm', sm: 'md' }}
            >
              Customisation rules
            </Button>
            <Box>
              <Text fontWeight="medium" textColor={'red.500'} fontSize={{ base: 'sm', sm: 'md' }}>
                Sold out
              </Text>
              <Switch
                isChecked={sold_out}
                onChange={async (e) => {
                  const newSoldOutValue = e.target.checked;
                  setSoldOut(newSoldOutValue);
                  handleInputChange('sold_out', newSoldOutValue);

                  if (selectedItem) {
                    await handleStockToggle(selectedItem, newSoldOutValue);
                  }
                }}
                size={{ base: 'sm', sm: 'md' }}
              />
            </Box>
          </VStack>

          {/* Modal for editing customization name */}
          <Modal isOpen={isEditModalOpen} onClose={closeEditModal} isCentered size={'xl'}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Edit Customization Name</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <FormControl id="customization-name" mb="4" isRequired>
                  <FormLabel>Customization Name</FormLabel>
                  <Input
                    placeholder="Enter customization name"
                    value={customizationData.customization}
                    onChange={(e) => handleInputChange('customization', e.target.value)}
                    variant={'filled'}
                    borderRadius={'full'}
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button
                  bg="black"
                  color={'white'}
                  _hover={{
                    color: 'black',
                    bg: 'white',
                    borderWidth: '1px',
                    borderColor: 'gray.300',
                  }}
                  variant="outline"
                  onClick={handleEditNameSave}
                  isLoading={isSavingName}
                  loadingText="Saving"
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Modal for creating or editing customization */}
          <Modal isOpen={isOpen && isNewCustomization} onClose={onClose} isCentered size={'xl'}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {isNewCustomization ? 'Create Customization' : 'Edit Customization'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <FormControl id="customization-name" mb="4" isRequired>
                  <FormLabel>Customization Name</FormLabel>
                  <Input
                    placeholder="Enter customization name"
                    value={customizationData.customization}
                    onChange={(e) => handleInputChange('customization', e.target.value)}
                    variant={'filled'}
                    borderRadius={'full'}
                  />
                </FormControl>

                <FormControl id="note" mb="4">
                  <FormLabel>Note</FormLabel>
                  <Input
                    placeholder="Optional note"
                    value={customizationData.note}
                    onChange={(e) => handleInputChange('note', e.target.value)}
                    variant={'filled'}
                    borderRadius={'full'}
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button
                  bg="black"
                  color={'white'}
                  _hover={{
                    color: 'black',
                    bg: 'white',
                    borderWidth: '1px',
                    borderColor: 'gray.300',
                  }}
                  variant="outline"
                  onClick={() => handleSubmit('create', customizationData)}
                >
                  Save
                </Button>
                <Button onClick={onClose} ml={3}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </SlideFade>
    </Box>
  );
};

export default CustomizationSideBox;

